import type { ReactElement } from 'react'
import { createRootRoute, createRoute, createRouter } from '@tanstack/react-router'
import { normalizeSearch } from '@ui/finance/search-params'
import { RequireAuth } from '@ui/auth/RequireAuth'
import { SignIn } from '@ui/auth/SignIn'
import { RootLayout } from '@ui/shell/RootLayout'
import { Settings } from '@ui/settings/Settings'
import { Classificar } from '@ui/finance/Classificar'
import { InstrucoesDeAgente } from '@ui/settings/InstrucoesDeAgente'
import { Cadastros } from './Cadastros'
import { Categorias } from './Categorias'
import { RegrasDeCategoria } from '@ui/settings/RegrasDeCategoria'
import { Dashboard } from './Dashboard'
import { Notificacoes } from './Notificacoes'
import { Relatorios } from './Relatorios'
import { Transacao } from './Transacao'
import { Transacoes } from './Transacoes'
import { Produtos } from './Produtos'
import { Produto } from './Produto'
import { Grupo } from './Grupo'
import { Notas } from './Notas'
import { Nota } from './Nota'
import { Lembretes } from './Lembretes'

const rootRoute = createRootRoute({ component: RootLayout })

/** A tela de login. Rota em português, como as outras (ADR-0009). O `destino`
 * é para onde voltar depois de entrar — ver `safeDestination`. */
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/entrar',
  component: SignIn,
  validateSearch: (search: Record<string, unknown>): { destino?: string } => {
    const destino = search['destino']
    return typeof destino === 'string' ? { destino } : {}
  },
})

/**
 * Rota de layout sem caminho (`id`, não `path`): ela não aparece na URL, só
 * embrulha as quatro abas no `RequireAuth`. É isso que faz "sem sessão,
 * qualquer rota leva ao login" valer por construção, e não rota a rota.
 */
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protegidas',
  component: RequireAuth,
})

// `path` precisa ser um generic `const` — se fosse tipado como `string` puro,
// o literal de cada chamada (ex.: '/transacoes') alargaria para `string` e o
// `createRoute` perderia o path exato, quebrando a tipagem de `<Link to=…>`
// em todas as rotas menos a raiz.
const makeRoute = <TPath extends string>(path: TPath, component: () => ReactElement) =>
  createRoute({ getParentRoute: () => protectedRoute, path, component })

/**
 * O mês selecionado vive na URL — `Docs/04-Design/Navegação.md` promete
 * `/transacoes?mes=2026-07`, e é isso que torna recarregar, voltar e
 * compartilhar link previsíveis.
 *
 * **Um `mes` inválido é descartado, não recusado.** O que chega na barra de
 * endereço é `unknown` de verdade, e derrubar a aba mais visitada do app por
 * causa de um caractere colado errado seria a resposta errada: sem `mes`
 * válido, a tela abre no mês corrente. É o mesmo critério de `safeDestination`
 * em `/entrar`.
 *
 * **`validateSearch` é uma allowlist, e isso mordeu.** O que ela não devolve é
 * **descartado** — a tela nunca vê. Ao acrescentar o filtro `?sem_categoria=1`,
 * ele funcionava pelo botão (que navega com a busca montada) e **não** ao colar
 * a URL, porque este validador só conhecia `mes` (decisão 142e).
 *
 * **É por isso que a allowlist NÃO mora mais aqui.** Ela é `normalizeSearch`,
 * em `@ui/finance/search-params`, ao lado de `searchFor`, que é quem monta a
 * URL do outro lado. Com um lado em cada arquivo, acrescentar um filtro é
 * lembrar de dois lugares e o teste do botão não pega o esquecimento; com os
 * dois juntos, há um caso de **ida e volta** que reprova quem lembrar pela
 * metade. O que sobra nesta rota é a ligação.
 */
const transacoesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/transacoes',
  component: Transacoes,
  validateSearch: normalizeSearch,
})

/**
 * O detalhe, empilhado sobre a aba de Transações — `Docs/04-Design/Navegação.md`
 * diz "tela empilhada, não aba", e é por isso que `TABS` continua com quatro
 * itens. Sem `validateSearch`: esta rota não tem busca, e o id vem do caminho.
 */
const transacaoRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/transacoes/$id',
  component: Transacao,
})

/** A revisão de uma nota, empilhada sobre Notas. */
const notaRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/notas/$id',
  component: Nota,
})

/** Um grupo de embalagens, empilhado sobre Produtos pelo mesmo motivo. */
const grupoRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/grupos/$id',
  component: Grupo,
})

/**
 * O catálogo, com a aba na URL.
 *
 * `Docs/04-Design/Navegação.md` já promete isso para o mês de Transações, e a
 * razão é a mesma: **recarregar, voltar e compartilhar link ficam
 * previsíveis**. Sem isto, quem abria uma embalagem e voltava caía em "O que a
 * casa compra" mesmo tendo saído de "Embalagens" — a aba era estado do
 * componente, e o histórico não tinha como saber dela.
 *
 * `validateSearch` é uma **allowlist**: o que ela não devolve, a tela não vê
 * (decisão 142e). E aba inválida é **descartada, não recusada** — o mesmo
 * critério do `mes`: um caractere colado errado não pode derrubar a tela, ela
 * só abre no padrão.
 */
const produtosRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/produtos',
  component: Produtos,
  validateSearch: (search: Record<string, unknown>): { aba?: 'embalagens' } =>
    // Só a que NÃO é padrão viaja. `/produtos` continua sendo a tela de grupos
    // (decisão 202), e um `?aba=grupos` explícito seria ruído numa URL que já
    // diz isso ao não dizer nada.
    search['aba'] === 'embalagens' ? { aba: 'embalagens' } : {},
})

/** O detalhe de produto, empilhado sobre Produtos pelo mesmo motivo. */
const produtoRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/produtos/$id',
  component: Produto,
})

export const routeTree = rootRoute.addChildren([
  signInRoute,
  protectedRoute.addChildren([
    makeRoute('/', Dashboard),
    transacoesRoute,
    transacaoRoute,
    produtosRoute,
    produtoRoute,
    grupoRoute,
    makeRoute('/notas', Notas),
    notaRoute,
    makeRoute('/lembretes', Lembretes),
    // Empilhada, não aba: `TABS` continua com quatro itens, e daqui se volta
    // para a aba de origem. Ver Docs/04-Design/Navegação.md.
    makeRoute('/ajustes', Settings),
    // Empilhada sobre Ajustes, e é de lá que se entra. `TABS` continua com
    // quatro itens. Ver Docs/04-Design/Navegação.md.
    makeRoute('/ajustes/agente', InstrucoesDeAgente),
    // Idem: a casa própria das categorias, que o comentário de `/cadastros`
    // logo abaixo já prometia. Entra-se por Ajustes.
    makeRoute('/ajustes/categorias', Categorias),
    makeRoute('/ajustes/regras', RegrasDeCategoria),
    // Empilhada, como Ajustes: entra-se pelo sino do Dashboard, e `TABS`
    // continua com quatro itens. Ver Docs/04-Design/Navegação.md.
    makeRoute('/notificacoes', Notificacoes),
    // Empilhada: entra-se pelo Dashboard. `TABS` continua com quatro itens.
    makeRoute('/relatorios', Relatorios),
    // Também empilhada, e **temporária**: é o andaime da Fase 2 para exercitar
    // o modelo (contas, cartões e categorias). Sai quando a Fase 4 der lugar
    // próprio a cada cadastro. Entra-se por Transações.
    makeRoute('/cadastros', Cadastros),
    // Empilhada: entra-se por Transações. `TABS` continua com quatro itens.
    makeRoute('/classificar', Classificar),
  ]),
])

/**
 * **`scrollRestoration` ligada.** Sem ela o router chama `scrollTo({ top: 0 })`
 * a cada rota renderizada — inclusive no voltar, que é justamente onde a
 * posição anterior era a resposta certa. Ligada, ela guarda a rolagem por
 * entrada do histórico e devolve; `useRestaurarRolagem` completa a parte que
 * ela sozinha não alcança, quando a lista chega depois do primeiro quadro.
 */
export const router = createRouter({ routeTree, scrollRestoration: true })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
